/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { FaqSection } from '../shared/models';

const TESTER_SUPPORT_LINK = '<a href="https://support.utest.com/csp" target="_blank" rel="noopener noreferrer">Tester Support</a>';

export const CERTIFICATION_FAQ_SECTIONS: FaqSection[] = [
  {
    heading: 'Introduction to Certifications',
    items: [
      {
        question: 'What are uTest Academy Certifications?',
        answer: 'Certifications are hands-on validation cycles where you apply the theory learned in the Academy to real-world scenarios. Think of them as "Live Labs" where you test real websites and apps to prove your testing proficiency, in a risk-free test cycle.',
      },
      {
        question: 'How do these differ from Learning Tracks?',
        answer: 'While <strong>Learning Tracks</strong> provide the knowledge through courses and quizzes, <strong>Certifications</strong> provide the experience. You will move from reading about testing to actually executing test cases and reporting bugs in a live environment, while being directed and supervised by a live team.',
      },
      {
        question: 'Is this real testing?',
        answer: 'Yes, you will be testing real functional websites and software, and you\'ll be doing it with other fellow testers who are on the same learning journey as you. The key difference is that these test cycles are managed by our Academy team and not for real customers, with the explicit purpose of ensuring a supportive environment focused on your professional growth.',
      },
    ],
  },
  {
    heading: 'Value & Benefits',
    items: [
      {
        question: 'Why should I get certified?',
        answer: 'Certifications are not only a great way to gain experience, but they can also help you get more cycle invitations under more projects. Obtaining a certification signals to our recruiters that you have the needed experience to participate in their projects. This might be especially important if you are new on uTest.',
      },
      {
        question: 'Will being certified help me earn more?',
        answer: 'Getting certified is the fastest way to increase your <strong>Tester Rating</strong>, which leads to more paid test cycle invitations. Additionally, a higher rating unlocks tier advancements, qualifying you for better payout rates for the exact same work.',
      },
      {
        question: 'How do Certifications help with paid projects?',
        answer: 'They act as a "Green Flag" for our recruiters. Being certified proves you understand our crowdtesting methods and best practices, reducing the chance of your work being rejected in paid test cycles.',
      },
      {
        question: 'How will I get better during the certification?',
        answer: 'In every certification cycle, the Academy team reviews your work. You get direct, professional feedback and mentorship, allowing you to learn from experts in a "safe harbor" before you join high-stakes projects.',
      },
      {
        question: 'Will I receive a formal certification?',
        answer: 'No. This program does not provide formal certifications. While the certifications are important to our recruiters, they are only meant for internal uses inside uTest.',
      },
    ],
  },
  {
    heading: 'The Certification Journey',
    items: [
      {
        question: 'How do I qualify for a certification?',
        answer: 'First, check the requirements for your chosen certification. You usually need to complete specific <strong>Learning Tracks</strong> and ensure your <strong>Tester Profile</strong> includes the required devices or testing environments. Some certifications may require other certifications as a prerequisite.',
      },
      {
        question: 'What is the "Knowledge Check"?',
        answer: 'Before registering, you must pass a short quiz to make sure you\'re ready for the testing tasks. Don\'t worry, everything you need to know is taught in the Academy courses. Once you pass, your "Sign Up" button will unlock.',
      },
      {
        question: 'What happens after I sign up?',
        answer: 'Once you sign up, you\'ll receive an <strong>invitation</strong> to join an Academy test cycle via the uTest platform. There, you\'ll be asked to review the cycle\'s detailed description and the tasks you\'ll need to complete, such as reporting issues, executing test cases, or submitting reviews. Once the requirements are clear, accept the invitation.',
      },
      {
        question: 'The test cycle shows "Pending Activation." What does that mean?',
        answer: '"Pending Activation" means the cycle is scheduled but not yet active. During this stage, you can review the overview and instructions and claim a slot in the cycle. Other tasks – including testing, reporting issues, executing test cases, or submitting reviews – will only be available once the cycle status changes to "Active".',
      },
      {
        question: 'How do I access the test cycle?',
        answer: 'You can access the test cycle from the Certification page or the Projects dashboard page. Simply click the test cycle name under the <strong>Test Cycles</strong> section, and you will be redirected to the cycle.',
      },
      {
        question: 'What are the requirements to "Pass" the Academy test cycle?',
        answer: 'The requirements vary depending on the specific certification test cycle. You can find all eligibility and passing criteria within the test cycle details before and during participation.',
      },
      {
        question: 'How will I officially know if I "Passed"?',
        answer: 'Once the Academy Team <strong>approves</strong> all required tasks – issue reports, test cases and so on – you passed the certification. You don\'t need to wait for the cycle to conclude; expect notification of your certification within 12–24 hours of approval. Note: all tasks must be completed within a single cycle. Reproducing another tester\'s approved report (aka, "+1") does not count toward your requirements.',
      },
      {
        question: 'Can I "Fail" a certification?',
        answer: 'To earn your certification, <strong>all tasks</strong> must be submitted while the cycle is active, and, if needed, fixed or provided with additional information before the cycle closes. If requirements are not met within this timeframe, the certification will not be awarded for this cycle. Don\'t worry: you may sign up to a future cycle to attempt the certification again.',
      },
      {
        question: 'Will I get paid for my approved work in the certification test cycles?',
        answer: 'No. The certification test cycles are offered for learning purposes only.',
      },
    ],
  },
  {
    heading: 'Participation & Logic',
    items: [
      {
        question: 'Are there different types of certifications?',
        answer: 'Most are <strong>Open Enrollment</strong> (register once prerequisites are met). However, we also feature <strong>Invite-Only Certifications</strong>, such as the "Academy All Stars" certification. These are exclusive, prestigious options reserved for the most consistent, high-quality Academy testers on the platform.',
      },
      {
        question: 'Can I retake a certification if I fail?',
        answer: 'Yes. There is no limit on attempts and no penalty for failing. Once the current cycle is closed, you can sign up again for the upcoming cycle.',
      },
      {
        question: 'Can I repeat a certification I\'ve already earned?',
        answer: 'Some certifications allow multiple attempts, while others are one-time only. Repeating a certification is a great way to refine your skills and boost your Tester Rating. Please note: intentionally failing tasks to trigger retakes is prohibited and may negatively impact your Tester Rating and overall standing.',
      },
      {
        question: 'What if I\'m not sure what to do? Who can help me?',
        answer: 'You have full access to the uTest Chat during your cycle. The Academy team is there to clarify requirements and provide guidance. You are part of a community, not testing in a vacuum.',
      },
    ],
  },
  {
    heading: 'Troubleshooting',
    items: [
      {
        question: 'I signed up but didn\'t get an invite. Where is it?',
        answer: `Check your spam folder for the invitation email, and re-examine the Invited and/or Pending tabs in the Test Cycles section of your uTest dashboard. If it's still missing, contact ${TESTER_SUPPORT_LINK} and file a ticket with "Academy On-Boarding & Training" set as the Request Category.`,
      },
      {
        question: 'What if I missed the window to accept my invitation?',
        answer: 'No problem. You won\'t be penalized. Just register for the next upcoming cycle and keep a close eye on your notifications so you can accept in a timely manner.',
      },
      {
        question: 'The system shows I\'m still signed up to a cycle I failed. What can I do?',
        answer: `Please wait until the cycle you have enrolled in is closed. Once the cycle is closed and all the grades are assigned, you will be able to sign up again. If it's not updating, contact ${TESTER_SUPPORT_LINK} and file a ticket with "Academy On-Boarding & Training" set as the Request Category.`,
      },
      {
        question: 'Am I allowed to use a VPN or proxy while testing?',
        answer: 'No. The use of VPNs or proxies is not allowed unless explicitly instructed by the testing team. Unauthorized use may result in account suspension.',
      },
    ],
  },
];
